/**
 * [TD-47.2a renamed in TD-47.5 Gap 6.B] TabbedPanel - generic tab-switcher.
 * Originally `RightRailTabs.jsx`; renamed because TD-47.5 mounts it at the
 * TOP of the right pane (not the bottom rail) AND it's now used for "your
 * data" tabs (Roster / Queue / History) rather than the old Activity / Chat
 * mix-bag. Internals unchanged from TD-47.2b - config-driven tabs[] array.
 *
 * Props:
 *   tabs            - Array<{id, label, icon, render: () => ReactNode}>
 *   defaultTab      - id of the tab shown initially (defaults to first)
 *   testIdPrefix    - defaults to "tabbed-panel"
 */
import { useState } from "react";
import TabErrorBoundary from "../analytics/TabErrorBoundary";

// [HF-POST-CHRIS] TabRenderer - intermediate descendant so error boundary
// actually catches throws. Wrapping `{fn()}` inline in TabbedPanel's own
// render put the throw in TabbedPanel's render phase, which boundaries
// cannot catch. Making the call happen INSIDE this child component moves
// the throw into a descendant render, which the boundary catches cleanly.
function TabRenderer({ render }) {
  return render ? render() : null;
}

export default function TabbedPanel({
  tabs = [],
  defaultTab,
  testIdPrefix = "tabbed-panel",
}) {
  const [active, setActive] = useState(defaultTab || tabs[0]?.id);
  const activeTab = tabs.find((t) => t.id === active) || tabs[0];

  if (!tabs.length) return null;

  return (
    <div className="flex flex-col h-full min-h-0" data-testid={testIdPrefix}>
      <div className="flex shrink-0 mb-1.5" data-testid={`${testIdPrefix}-header`}>
        {tabs.map((tab, idx) => {
          const isActive = active === tab.id;
          const Icon = tab.icon;
          const isFirst = idx === 0;
          const isLast = idx === tabs.length - 1;
          const corner = isFirst ? "rounded-l-md" : isLast ? "rounded-r-md" : "";
          const borderClass = isFirst ? "border" : "border-y border-r";
          return (
            <button
              key={tab.id}
              onClick={() => setActive(tab.id)}
              data-testid={`${testIdPrefix}-tab-${tab.id}`}
              data-active={isActive ? "true" : undefined}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-[10px] font-display font-black uppercase tracking-[0.18em] ${corner} ${borderClass} transition-colors ${
                isActive
                  ? "bg-[#FF6B00]/15 border-[#FF6B00]/50 text-[#FF6B00]"
                  : "bg-[#1C1C1C] border-[#252525] text-[rgba(255,255,255,0.45)] hover:text-[rgba(255,255,255,0.75)]"
              }`}
            >
              {Icon && <Icon size={11} />}
              {tab.label}
            </button>
          );
        })}
      </div>
      <div className="flex-1 min-h-0">
        <TabErrorBoundary resetKey={active}>
          <TabRenderer render={activeTab?.render} />
        </TabErrorBoundary>
      </div>
    </div>
  );
}
